/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { BuildingOffice2Icon, UserCircleIcon } from "@heroicons/react/24/outline";
import {
  Button,
  Flex
} from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  queryClient, useAllAutomations, useAllPlaybooks,
  useTraitsAndMetrics
} from "../../../../hooks/QueryHooks";
import { updateAutomation } from "../../../../services/automation/automationEndpoints";
import { getWorkspaceTrendsAgaintQuery } from "../../../../services/workspace/workspaceEndpoints";
import { ENTITY } from "../../../../services/workspace/workspaceEndpoints.enums";
import MButton from "../../../../shared/ui/buttons/MButton";
import useSignalStore from "../../../../store/signal/signalStore";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import { getLargerTimeWindow } from "../../../../utils/dateConverter";
import notify from "../../../../utils/notify";
import NewSignalBody from "./components/NewSignalBody";

const typeChoices = [
  { value: "accounts", label: "Accounts", Icon: BuildingOffice2Icon },
  { value: "users", label: "Users", Icon: UserCircleIcon },
];

const basedOnChoices = [
  { value: "usage_based", label: "Usage - hits a usage threshold" },
  { value: "event_based", label: "Event - does a specific action" },
];

const EditSignalPage = () => {
  const {
    signal,
    setSignal,
    setName,
    setPlaybook,
    setEntityRelation,
    setTrigger,
    setMetrics,
    setSelectedFields,
    addFilter,
    addPredicate,
    editPredicate,
    deletePredicate,
    deleteFilter,
    clearFilter,
    clearSignal,
    setTimeWindow,
    setSuppressionPeriod,
    createAction,
    deleteAction,
    updateAction,
    setResponseData,
  } = useSignalStore();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const params = useParams();
  const { data: signals, isSuccess }: any = useAllAutomations({
    workspaceId: workspace?.id,
    automationType: "signal",
    enabled: !!workspace?.id,
  });

  const [error, setError] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);

  useEffect(() => {
    if (params?.signalId && isSuccess) {
      const selectedSignal = signals?.find((sig: any) => sig.id === params?.signalId);
      if (selectedSignal) {
        // Ensure sort object exists with proper defaults
        const defaultSort = {
          field: selectedSignal.entity_relation === "accounts" ? "default_metric_total_active_users" : "default_metric_days_active",
          order: "desc",
          type: "changeval",
        };
        
        const existingSort = selectedSignal.sort || defaultSort;
        
        // If sort field is a metric, ensure type is changeval
        const metrics = selectedSignal.metrics || [];
        if (metrics.some((metric: string) => existingSort.field.includes(metric))) {
          existingSort.type = "changeval";
        }
        
        const signalWithSort = {
          ...selectedSignal,
          sort: existingSort,
          // Ensure metrics has proper defaults
          metrics: selectedSignal.metrics && selectedSignal.metrics.length > 0 
            ? selectedSignal.metrics 
            : [selectedSignal.entity_relation === "accounts" ? "default_metric_total_active_users" : "default_metric_days_active"]
        };
        setSignal(signalWithSort);
      }
    }
  }, [params?.signalId, isSuccess]);

  const traitsAndMetrics: any = useTraitsAndMetrics({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const { data: playBookData }: any = useAllPlaybooks({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  useAllAutomations({
    workspaceId: workspace?.id,
    automationType: "signal",
    enabled: !!workspace?.id,
  });

  const playbooks = useMemo(() => playBookData?.map((playbook: any) => ({
    label: playbook?.title,
    value: playbook?.id,
  })), [playBookData?.length]);

  const updateSignalMutation = useMutation({
    mutationFn: updateAutomation,
  });

  const getTotalMutation = useMutation({
    mutationFn: getWorkspaceTrendsAgaintQuery,
  });

  const handleFilterAdd = () => {
    const selectedFilters = signal
      .filters.predicates[signal.filters.predicates.length - 1]?.predicates || [];
    if (selectedFilters.length === 0 || selectedFilters[0].field !== "") {
      addFilter({
        query_type: "AND",
        predicates: [{
          field: "",
          operator: "",
          value: "",
          type: "",
          entity_type: "column",
        }],
      });
    }
  };

  const handlePredicateAdd = (filterId?: any) => {
    const selectedFilters = signal.filters.predicates[filterId].predicates;
    if (selectedFilters.length === 0 || selectedFilters[selectedFilters.length - 1].field !== "") {
      addPredicate(
        {
          field: "",
          operator: "",
          value: "",
          type: "",
          entity_type: "column",
        },
        filterId,
      );
    }
  };

  const handlePredicateClear = (filterId?: any) => {
    deleteFilter(filterId);
  };

  const handlePredicateChange = (
    field: string,
    operator: string,
    value: any,
    type: string,
    entity_type?: string,
    id?: any,
    filterId?: any,
    time_window?: string,
  ) => {
    editPredicate({
      field,
      operator,
      value,
      type,
      entity_type: entity_type || "column",
      time_window,
    }, id, filterId);
    const selectedColumns = signal?.selected_fields || [];
    const selectedMetrics = signal?.metrics || [];
    let newColumns: any = selectedColumns;
    const newMetrics: any = (!selectedMetrics.includes(field) && entity_type !== "column" && field !== "") ? [...selectedMetrics, field] : selectedMetrics;
    if (signal?.entity_relation === "accounts" && field.startsWith("accounts.")) {
      newColumns = (!selectedColumns.includes(field) && entity_type === "column" && field !== "") ? [...selectedColumns, field] : selectedColumns;
      setSelectedFields(newColumns);
    } else if (signal?.entity_relation === "users" && field.startsWith("users.")) {
      newColumns = (!selectedColumns.includes(field) && entity_type === "column" && field !== "") ? [...selectedColumns, field] : selectedColumns;
      setSelectedFields(newColumns);
    }
    setMetrics(newMetrics);
    setTimeWindow(getLargerTimeWindow(signal?.time_window, time_window));
  };

  const handlePredicateRemove = (id: number, filterId?: any) => {
    deletePredicate(id, filterId);
  };

  const navigate = useNavigate();

  const onTimeWindowChange = (value: any) => {
    setTimeWindow(value);
  };

  const handleEntityChange = (value: "accounts" | "users") => {
    if (value === "accounts" && signal.entity_relation !== "accounts") {
      setSelectedFields(["accounts.name", "accounts.last_seen", "accounts.created_at"]);
    } else if (value === "users" && signal.entity_relation !== "users") {
      setSelectedFields(["users.name", "users.first_name", "users.last_seen", "users.created_at"]);
    }
    setEntityRelation(value);
  };

  const onSignalSubmit = () => {
    const slackEnabled = signal?.automation_actions?.actions?.find((action: any) => action?.type === "send_to_slack");
    const emailEnabled = signal?.automation_actions?.actions?.find((action: any) => action?.type === "send_to_email");
    const inboxEnabled = signal?.automation_actions?.actions?.find((action: any) => action?.type === "send_to_inbox");
    if (!signal?.name || !signal?.entity_relation || !signal?.trigger
      || !signal?.filters?.predicates[0]?.predicates?.length
      || !signal?.automation_actions?.actions?.length
      || !signal?.automation_actions?.suppression_period
      || !signal?.time_window
      || (!!slackEnabled && ((slackEnabled?.message_type === "channel" && !slackEnabled?.channel_id)
        || (slackEnabled?.message_type === "direct" && !slackEnabled?.slack_user_id)))
      || (!!emailEnabled && (!emailEnabled?.to || !emailEnabled?.subject))
      || (!signal?.filters?.predicates?.find((fil: any) => fil?.predicates?.find((item: any) => item?.field?.includes("metric"))))) {
      setError(true);
      notify({
        messageList: ["Kindly resolve errors in form fields"],
        type: "warning",
      });
    } else {
      // Fix sort before submission
      const sortConfig = {
        field: signal.sort?.field || "",
        order: signal.sort?.order || "desc",
        type: signal.sort?.type || "property",
      };

      // If sort field is empty or invalid, set default based on entity type
      if (!sortConfig.field || sortConfig.field === "") {
        sortConfig.field = signal.entity_relation === "accounts" ? "default_metric_total_active_users" : "default_metric_days_active";
        sortConfig.order = "desc";
        sortConfig.type = "changeval";
      }

      // Ensure selected_fields is not empty - use empty array as default
      const selectedFields = signal.selected_fields && signal.selected_fields.length > 0
        ? signal.selected_fields
        : [];

      // Ensure metrics is not empty - use default metric based on entity type
      const metrics = signal.metrics && signal.metrics.length > 0
        ? signal.metrics
        : [signal.entity_relation === "accounts" ? "default_metric_total_active_users" : "default_metric_days_active"];

      // Update sort type for metrics
      if (metrics.length > 0) {
        const isMetricSort = metrics.some((metric: string) => sortConfig.field.includes(metric));
        if (isMetricSort) {
          sortConfig.type = "changeval";
        }
      }

      updateSignalMutation.mutate({
        ...signal,
        selected_fields: selectedFields,
        metrics,
        sort: sortConfig,
        automation_id: params?.signalId || "",
        time_zone: workspace?.workspace_time_zone,
        status: "active",
        workspace_id: workspace?.id,
      }, {
        onSuccess: (data: any) => {
          queryClient.setQueryData(["all_automations", workspace?.id, signal?.automation_type], (oldData: any) => ({
            ...oldData,
            data: oldData?.data?.map(
              (item: any) => (item?.id === data?.data?.id ? data?.data : item),
            ),
          }));
          if (process.env.REACT_APP_HE_WORKSPACE_KEY && process.env.REACT_APP_HE_DATA_KEY) {
            window?.hyperengage(
              "track",
              "signal_updated",
              {
                properties: {
                  name: signal?.name,
                  entity_relation: signal?.entity_relation,
                  automation_id: params?.signalId || "",
                  trigger: signal?.trigger,
                  actions: signal?.automation_actions?.actions?.map((action: any) => action?.type),
                  time_zone: workspace?.workspace_time_zone,
                  status: "active",
                  workspace_id: workspace?.id,
                },
              },
            );
          }
          notify({
            type: "success",
            messageList: ["Signal updated successfully."],
          });
          navigate(-1);
        },
        onError: (err: any) => {
          if (err.response?.status !== 0) {
            notify({
              heading: "Unknown error.",
              type: "failure",
              messageList: [
                "An unknown error occurred while updating the signal. Please try again later or contact our support team for assistance.",
              ],
            });
          }
        },
      });
    }
  };

  useEffect(
    () => () => { clearSignal(); },
    [],
  );

  return (
    <div className="px-5 overflow-auto relative h-full bg-gray-100">
      <div className="max-w-6xl mx-auto">
        <NewSignalBody
          setSelectedFields={setSelectedFields}
          setSelectedMetrics={setMetrics}
          clearFilter={clearFilter}
          clearSignal={clearSignal}
          onTimeWindowChange={onTimeWindowChange}
          handlePredicateAdd={handlePredicateAdd}
          handlePredicateClear={handlePredicateClear}
          handlePredicateRemove={handlePredicateRemove}
          handlePredicateChange={handlePredicateChange}
          handleFilterAdd={handleFilterAdd}
          setPlaybook={setPlaybook}
          onSignalSubmit={onSignalSubmit}
          error={error}
          total={total}
          traitsAndMetrics={traitsAndMetrics}
          signal={signal}
          setName={setName}
          setEntityRelation={handleEntityChange}
          setTrigger={setTrigger}
          setSuppressionPeriod={setSuppressionPeriod}
          createAction={createAction}
          deleteAction={deleteAction}
          updateAction={updateAction}
          playbooks={playbooks}
        />
        <Flex justify="flex-end" gap={5} className="mt-2 p-4 bg-gray-100">
          <Button
            variant="outline"
            onClick={() => {
              clearSignal();
              navigate(-1);
            }}
            className="hover:bg-gray-100"
            color="gray"
            disabled={updateSignalMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={onSignalSubmit}
            loading={updateSignalMutation.isLoading}
          >
            {updateSignalMutation.isLoading ? "Updating..." : "Update Signal"}
          </Button>
        </Flex>
      </div>
    </div>
  );
};

export default EditSignalPage;
